import { useState, FormEvent, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MhProduct, ParamConfigItem } from '@/types/mh';
import { getToken } from '@/lib/auth-storage';
import { openSaleSmartlyChat } from '@/lib/salesmartly';
import { createOrderApi, fetchOrderSubmitMode } from '@/api/order';
import { formatProductPriceAmount, formatProductPriceUnit } from '@/utils/product-display';
import { profileApi, type AppProfile } from '@/api/auth';

const PARAM_LABEL_FALLBACK: Record<string, string> = {
  phone: '手机号',
  id_card: '身份证号',
  idCard: '身份证号',
  idCardTail: '身份证后四位',
  idcard: '身份证号',
  idcard_like: '身份证号',
  bank_card: '银行卡号',
  bankCard: '银行卡号',
  wechat: '微信号',
  qq: 'QQ号',
  email: '邮箱',
  name: '姓名',
  address: '地址',
};

function parseParamConfig(json: string): ParamConfigItem[] {
  try {
    const arr = JSON.parse(json) as unknown;
    if (!Array.isArray(arr)) {
      return [];
    }
    return arr.filter((x): x is ParamConfigItem => {
      if (!x || typeof x !== 'object') {
        return false;
      }
      const o = x as Record<string, unknown>;
      return typeof o.key === 'string' && o.key.length > 0;
    });
  } catch {
    return [];
  }
}

function fieldLabel(field: ParamConfigItem): string {
  return field.label?.trim() || PARAM_LABEL_FALLBACK[field.key] || field.key;
}

/** 客服消息用：U → RMB 统一按 7 换算 */
const CS_USDT_TO_RMB_RATE = 7;

function formatCsMoney(n: number): string {
  if (!Number.isFinite(n)) {
    return '-';
  }
  return String(Number(n.toFixed(2)));
}

function buildAmountLine(price: number | string | null | undefined): string {
  const u = Number(price);
  if (!Number.isFinite(u)) {
    return '金额：- U/ - RMB';
  }
  const rmb = u * CS_USDT_TO_RMB_RATE;
  return `金额：${formatCsMoney(u)} U/ ${formatCsMoney(rmb)} RMB`;
}

function buildP2pChatMessage(
  product: MhProduct,
  fields: ParamConfigItem[],
  params: Record<string, string>,
  profile: AppProfile,
): string {
  const lines = [
    `客户来源：【${profile.partnerName?.trim() || '无'}】`,
    '产品咨询',
    `用户ID：${profile.userId}`,
    `用户账号：${profile.userName || '-'}`,
    `产品编码：${product.productCode}`,
    `产品名称：${product.productName}`,
    buildAmountLine(product.price),
    '下单信息：',
  ];
  for (const field of fields) {
    const value = params[field.key] || '';
    if (value.trim()) {
      lines.push(`${fieldLabel(field)}：${value.trim()}`);
    }
  }
  return lines.join('\n');
}

interface QueryFormProps {
  product: MhProduct;
  className?: string;
  formId?: string;
  showSubmitButton?: boolean;
}

export default function QueryForm({
  product,
  className = '',
  formId,
  showSubmitButton = true,
}: QueryFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const fields = useMemo(() => parseParamConfig(product.paramConfig || '[]'), [product.paramConfig]);

  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [jumpCustomerService, setJumpCustomerService] = useState(true);

  useEffect(() => {
    setError('');
  }, [product.productId]);

  useEffect(() => {
    let cancelled = false;
    fetchOrderSubmitMode()
      .then((res) => {
        if (cancelled || res.code !== 0 || !res.data) return;
        setJumpCustomerService(res.data.jumpCustomerService !== false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const validate = (): boolean => {
    for (const f of fields) {
      if (f.required && !String(values[f.key] || '').trim()) {
        setError(`请填写：${f.label || f.key}`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    if (!validate()) {
      return;
    }
    if (!getToken()) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }

    const params: Record<string, string> = {};
    for (const f of fields) {
      const v = values[f.key];
      if (v !== undefined && v !== '') {
        params[f.key] = v.trim();
      }
    }

    if (!jumpCustomerService) {
      setSubmitting(true);
      try {
        const orderRes = await createOrderApi({ productId: product.productId, params });
        if (orderRes.code !== 0) {
          setError(orderRes.msg || '下单失败');
          return;
        }
        navigate('/orders');
      } catch {
        setError('下单失败，请稍后重试');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    let profile: AppProfile;
    try {
      const profileRes = await profileApi();
      if (profileRes.code !== 0 || !profileRes.data?.userId) {
        setError(profileRes.msg || '无法获取用户信息，请重新登录后再试');
        return;
      }
      profile = profileRes.data;
    } catch {
      setError('无法获取用户信息，请重新登录后再试');
      return;
    }

    openSaleSmartlyChat({ message: buildP2pChatMessage(product, fields, params, profile) });
  };

  const priceAmount = formatProductPriceAmount(product.price, product);
  const priceUnit = formatProductPriceUnit(product);

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 p-6 ${className}`}>
      <h3 className="text-lg font-bold text-gray-900 mb-5">立即查询</h3>
      <p className="text-xs text-gray-400 mb-4">
        {jumpCustomerService ? (
          <>
            点对点服务，提交后将为您接通客服，费用为{' '}
            <span className="font-medium text-gray-600">
              {priceAmount} {priceUnit}
            </span>
            起。
          </>
        ) : (
          <>
            提交后将从余额扣除{' '}
            <span className="font-medium text-gray-600">
              {priceAmount} {priceUnit}
            </span>
            。余额不足请先充值。
          </>
        )}
      </p>

      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        {fields.length === 0 ? (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            该产品未配置下单参数，请联系管理员在后台填写「参数配置」JSON。
          </p>
        ) : (
          fields.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {f.label || f.key}
                {f.required && <span className="text-red-500"> *</span>}
              </label>
              <input
                type="text"
                name={f.key}
                value={values[f.key] ?? ''}
                onChange={(e) => setField(f.key, e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all"
                autoComplete="off"
              />
            </div>
          ))
        )}

        {error && (
          <div className="text-sm text-red-500 text-center">
            <p>{error}</p>
            {error.includes('余额不足') && (
              <button
                type="button"
                className="mt-1 underline cursor-pointer"
                onClick={() => navigate('/recharge')}
              >
                去充值
              </button>
            )}
          </div>
        )}

        {showSubmitButton && (
          <button
            type="submit"
            disabled={fields.length === 0 || submitting}
            className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            {submitting ? '提交中…' : '提交订单'}
          </button>
        )}
      </form>

      {!getToken() && (
        <p className="mt-3 text-xs text-center text-gray-400">
          提交前请先
          <button
            type="button"
            className="text-gray-700 underline mx-0.5 cursor-pointer"
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`)}
          >
            登录
          </button>
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-center space-x-5 text-xs text-gray-400">
          <div className="flex items-center space-x-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line text-gray-400" />
            </div>
            <span>安全请求，SSL 加密</span>
          </div>
          <div className="flex items-center space-x-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-lock-line text-gray-400" />
            </div>
            <span>默认隐私与保密</span>
          </div>
        </div>
      </div>
    </div>
  );
}
